import React from 'react';
import { Box } from '@chakra-ui/react';
import styled from 'styled-components';

import { Button, Heading, Paragraph } from 'components';

import Icon from 'components/Icon';
import { isMobile } from 'styles/utils';

type PlanProps = {
  name: React.ReactNode;
  subname: string;
  description: string | React.ReactNode;
  cost: React.ReactNode;
  disabled?: boolean;
  details: string | React.ReactNode;
  isActivePlan?: boolean;
  features: React.ReactNode[];
  onSelect: () => void;
  planColor: string;
  withoutButton?: boolean;
  buttonText: string;
  buttonVariant?: string;
  style?: React.CSSProperties;
  planNameMargin?: string;
  dataTestId?: string;
  planIncludes?: React.ReactNode;
  planNameColor?: string;
};

export const PlanName = styled(Heading)`
  text-align: center;
  color: ${props => props.planColor || props.theme.color.wfBlack};
`;

export const PlanCost = styled(Heading)`
  margin-bottom: 0.3em;
  text-align: center;
  font-size: 18px;
`;

const PlanCostDetails = styled(Paragraph)`
  margin-bottom: 1em;
  text-align: center;
  opacity: 0.4;
  font-size: 12px;
`;

const PlanDescription = styled(Paragraph)`
  font-size: 15px;
  text-align: center;
  margin-bottom: 1em;
  margin-top: 1em;
`;

const PlanIncludes = styled(Paragraph)`
  font-size: 14px;
  text-align: center;
  margin-bottom: 1em;
  margin-top: 1em;
`;

const PlanColumn = styled.div`
  display: flex;
  flex-direction: column;
`;
const PlanContent = styled.div`
  padding-top: 1em;
  padding-bottom: 1em;
  padding-left: 1.5em;
  padding-right: 1.5em;
`;

const StyledIcon = styled(Icon)`
  margin-right: 0.5em;
  transform: translateY(3px);
  width: 16px;
  height: 16px;
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 3em;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.2em;
`;

const PlanButton = styled(Button)`
  /*Position the button at the bottom of the column and center horizontally*/
  max-width: 200px;
  margin-top: auto;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 1em;
  display: block;
  align-self: flex-end;
`;

export const Plan: React.FC<PlanProps> = ({
  name,
  subname,
  description,
  cost,
  details,
  features,
  onSelect,
  planColor,
  buttonText,
  withoutButton,
  disabled,
  buttonVariant,
  style,
  planNameMargin,
  isActivePlan = false,
  dataTestId,
  planIncludes,
}) => {
  const featureComponents = features?.map((feature, idx) => (
    <FeatureItem key={feature.toString() + idx}>
      <StyledIcon name='check' />
      <div className='text-black'>{feature}</div>
    </FeatureItem>
  ));

  return (
    <PlanColumn
      style={{
        ...style,
        position: 'relative',
        borderStyle: 'solid',
        borderWidth: '1px',
        height: isMobile() ? '34rem' : '40.9375rem',
        width: '20.375rem',
        minWidth: 294,
        borderColor: planColor || '#172DF5',
      }}
    >
      <Box
        w='100%'
        h='8px'
        bg={planColor || '#172DF5'}
        marginBottom={planNameMargin || '2em'}
      />
      <PlanContent>
        <PlanName size='large' planColor={planColor}>
          {name}
        </PlanName>
        {subname ? (
          <div className='font-bold text-xs text-center text-black mt-0'>
            {subname}
          </div>
        ) : null}
        {cost}
        {details && <PlanCostDetails>{details}</PlanCostDetails>}
        {description ? <PlanDescription>{description}</PlanDescription> : null}
        {planIncludes && <PlanIncludes>{planIncludes}</PlanIncludes>}

        {featureComponents ? (
          <FeaturesList>{featureComponents}</FeaturesList>
        ) : null}

        {isActivePlan ? (
          <div className='text-black text-center font-semibold absolute w-4/5 bottom-7'>
            Active Plan
            <Icon className='ml-4' name='check' />
          </div>
        ) : (
          <PlanButton
            data-testid={dataTestId}
            size='small'
            disabled={disabled}
            text={buttonText || 'Get Started'}
            variant={buttonVariant || 'transparent'}
            style={{
              borderColor: planColor || '#172DF5',
              borderWidth: '1px',
              borderStyle: 'solid',
              display: withoutButton ? 'none' : 'block',
              position: 'absolute',
              marginTop: 10,
              bottom: 10,
              left: '50%',
              width: 160,
              transform: 'translateX(-50%)',
            }}
            onClick={() => {
              if (onSelect) {
                onSelect();
              }
            }}
          />
        )}
      </PlanContent>
    </PlanColumn>
  );
};
